'use memo'

import { useTypes, type Type } from '@sportninja/common/hooks/useTypes'
import type { SportPosition } from '@sportninja/common/features/teamFinder/positionFilter'
import { useEffect } from 'react'

interface PlayerType {
  id: string | number
  name?: string | null
  name_full?: string | null
}

/**
 * Position options for a sport, from the `/sports/types` payload.
 *
 * NOT `usePositionOptions` (the roster's hook): that reads the Redux
 * `sports[id].players` map, which is only populated by a per-sport
 * `typesBySport` fetch that nothing on this page performs — so it comes back
 * empty here no matter which sport is picked. `/sports/types` carries every
 * sport's player types in one call and is what this form already loads.
 */
export const useSportPositionOptions = (
  sportId: string | null
): SportPosition[] => {
  'use memo'

  const { types, fetchTypes } = useTypes()

  useEffect(() => {
    if (!types || types.length === 0) {
      fetchTypes()
    }
  }, [types, fetchTypes])

  if (!sportId || !types) {
    return []
  }

  const sportType = (types as Type[]).find(
    (t) => t.sport_id === sportId.toString()
  )
  const playerTypes: PlayerType[] =
    sportType?.data?.find((d: any) => d.player_types !== undefined)
      ?.player_types ?? []

  return playerTypes.map((p) => ({
    label: p.name_full ?? p.name ?? String(p.id),
    value: String(p.id),
  }))
}
